import React, { FC, useContext } from "react";
import { defineMessages, useIntl } from "react-intl";
import Svg from "@natera/material/lib/svg";
import MagnifierIcon from "@app/postBill/assets/svg/magnifier-icon.svg";
import { IntlContext } from "@app/provider";
import { getFormattedLocalizeDate } from "@app/utils";
import { convertBalance } from "@app/postBill/utils/convertBalance";
import StatusChip from "@app/postBill/components/statusChip";

import "./billingStatementCard.scss";
import { StatementStatus } from "@app/postBill/models";

const messages = defineMessages({
  billingStatementCardCaseNumber: {
    id: "billingStatementCardCaseNumber",
    defaultMessage: "Case number",
  },
  billingStatementCardViewStatement: {
    id: "billingStatementCardViewStatement",
    defaultMessage: "View statement",
  },
  billingStatementCardDateOfService: {
    id: "billingStatementCardDateOfService",
    defaultMessage: "Date of services",
  },
  billingStatementCardOrderedBy: {
    id: "billingStatementCardOrderedBy",
    defaultMessage: "Ordered by",
  },
});

interface BillingStatementCardProps {
  caseNumber: string;
  testFullName: string;
  amountBilled: number;
  currency: string;
  dateOfService: string;
  orderedBy: string;
  paidStatus: StatementStatus;
  hasPdf: boolean;
  onViewStatement: () => void;
}

const BillingStatementCard: FC<BillingStatementCardProps> = ({
  caseNumber,
  testFullName,
  amountBilled,
  currency,
  dateOfService,
  orderedBy,
  paidStatus,
  hasPdf,
  onViewStatement,
}) => {
  const intl = useIntl();
  const { currentLanguage } = useContext(IntlContext);

  return (
    <div className="billing-history-card" data-testid="statement-card">
      <div className="billing-history-card__display">
        <div className="billing-history-card__header">
          <div className="billing-history-card__title">{testFullName}</div>
          <div className="billing-history-card__amount">
            {convertBalance(amountBilled, true, currency)}
          </div>
        </div>
        {paidStatus === StatementStatus.READY_TO_PAY ? null : (
          <div
            className="billing-history-card__status"
            data-testid="paid-status"
          >
            <StatusChip status={paidStatus} />
          </div>
        )}
        <div className="billing-history-card__content">
          <div className="billing-history-card__row">
            <span className="billing-history-card__label">
              {intl.formatMessage(messages.billingStatementCardCaseNumber)}
            </span>
            <span className="billing-history-card__value">{caseNumber}</span>
          </div>
          <div className="billing-history-card__row">
            <span className="billing-history-card__label">
              {intl.formatMessage(messages.billingStatementCardDateOfService)}
            </span>
            <span className="billing-history-card__value">
              {getFormattedLocalizeDate(dateOfService, currentLanguage)}
            </span>
          </div>
          <div className="billing-history-card__row">
            <span className="billing-history-card__label">
              {intl.formatMessage(messages.billingStatementCardOrderedBy)}
            </span>
            <span className="billing-history-card__value">{orderedBy}</span>
          </div>
        </div>
        {hasPdf && (
          <button
            className="billing-history-card__button"
            onClick={onViewStatement}
            data-testid="view-statement-button"
          >
            <Svg
              content={MagnifierIcon}
              className="billing-history-card__button-icon"
            />
            {intl.formatMessage(messages.billingStatementCardViewStatement)}
          </button>
        )}
      </div>
    </div>
  );
};

export default BillingStatementCard;
